import { motion } from "framer-motion";
import { ChevronDownIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ErrorMessage } from "@/app/components/error-message";
import { LoadingScreen } from "@/components/loading-screen";
import { Button } from "@/components/ui/button";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { cn } from "@/lib/utils";

import { useFetchRecordingComments } from "../api/fetch-recording-comments";

import { CommentItem } from "./comment-item";

type RepliesProps = {
  parentCommentId: string;
  handleJumpToTimestamp: (timestamp: number) => void;
};

export function Replies(props: RepliesProps) {
  const { parentCommentId, handleJumpToTimestamp } = props;
  const { id: recordingId } = useParams();
  const [isOpen, setIsOpen] = useState(true);
  const { organisation } = useOrganisation();
  const {
    data: comments,
    isLoading,
    error,
    isError,
    isSuccess,
  } = useFetchRecordingComments(
    {
      organisationId: organisation.id,
      recordingId: recordingId || "",
    },
    {
      select: (comments) => {
        return comments.filter(
          (comment) => comment.parentCommentId === parentCommentId,
        );
      },
    },
  );

  useEffect(() => {
    if (comments && comments.length > 1) {
      setIsOpen(false);
    }
  }, [comments]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isError || !isSuccess) {
    return <ErrorMessage error={error} />;
  }

  if (comments.length === 0) {
    return null;
  }

  return (
    <div>
      <Button
        type="button"
        variant="link"
        className="mb-4 mt-2 h-auto p-0 hover:bg-transparent"
        onClick={() => setIsOpen(!isOpen)}
      >
        <ChevronDownIcon
          className={cn(
            "mr-2 h-4 w-4 transition-transform",
            isOpen && "rotate-180",
          )}
        />
        {comments.length} {comments.length === 1 ? "Reply" : "Replies"}
      </Button>
      <motion.div
        className="space-y-2 overflow-hidden"
        initial={{ opacity: 0, height: 0 }}
        animate={
          isOpen ? { opacity: 1, height: "auto" } : { opacity: 0, height: 0 }
        }
        transition={{ duration: 0.3 }}
      >
        {comments.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            handleJumpToTimestamp={handleJumpToTimestamp}
          />
        ))}
      </motion.div>
    </div>
  );
}
